import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, SignOut, ShieldCheck, Stack, BookBookmark, GraduationCap, PlayCircle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user && user.role === "admin";
  // Highlighted-pulse on the Knowledge Base link when the user hasn't filled
  // it yet. AuthContext fetches `knowledge_base_filled` after /auth/me.
  const kbIncomplete = user && user !== false && user.knowledge_base_filled === false;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="modern-nav" data-testid="app-navbar">
      <div className="modern-nav-inner">
        <Link to="/" className="modern-brand" data-testid="navbar-logo-link">
          <img
            src="/adxsk-logo.png"
            alt="Akshat Dani x SK"
            className="modern-brand-logo modern-brand-logo-light"
          />
          <img
            src="/adxsk-logo-dark.png"
            alt="Akshat Dani x SK"
            className="modern-brand-logo modern-brand-logo-dark"
          />
        </Link>

        <nav className="modern-nav-center">
          <Link
            to="/"
            data-testid="navbar-link-gpts"
            className={`modern-nav-link ${location.pathname === "/" ? "is-active" : ""}`}
          >
            <Stack size={16} weight="duotone" /> Dashboard
          </Link>
          {user && user !== false && (
            <Link
              to="/knowledge-base"
              data-testid="navbar-link-knowledge-base"
              className={`modern-nav-link ${location.pathname.startsWith("/knowledge-base") ? "is-active" : ""} ${kbIncomplete ? "nav-link-pulse" : ""}`}
              title={kbIncomplete ? "Fill your knowledge base so every GPT knows your background" : "Knowledge Base"}
            >
              <BookBookmark size={16} weight="duotone" />
              Knowledge Base
              {kbIncomplete && (
                <span
                  className="nav-link-dot"
                  aria-label="Knowledge base incomplete"
                  data-testid="kb-incomplete-dot"
                />
              )}
            </Link>
          )}
          {user && user !== false && (
            <Link
              to="/resources"
              data-testid="navbar-link-resources"
              className={`modern-nav-link ${location.pathname.startsWith("/resources") ? "is-active" : ""}`}
            >
              <PlayCircle size={16} weight="duotone" /> Resources
            </Link>
          )}
          {user && user !== false && user.bootcamps_unlocked && (
            <Link
              to="/bootcamps"
              data-testid="navbar-link-bootcamps"
              className={`modern-nav-link ${location.pathname.startsWith("/bootcamps") ? "is-active" : ""}`}
            >
              <GraduationCap size={16} weight="duotone" /> Bootcamps
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin"
              data-testid="navbar-link-admin"
              className={`modern-nav-link ${location.pathname.startsWith("/admin") ? "is-active" : ""}`}
            >
              <ShieldCheck size={16} weight="duotone" /> Admin
            </Link>
          )}
        </nav>

        <div className="modern-nav-actions">
          {user && user !== false && (
            <div className="modern-user-pill" data-testid="navbar-user-info">
              <div className="modern-user-text hidden sm:block">
                <div className="modern-user-name">{user.name}</div>
                <div className={`modern-user-role ${isAdmin ? "" : "is-user"}`}>
                  {user.role}
                </div>
              </div>
              <div className="modern-avatar">{user.name.slice(0, 1).toUpperCase()}</div>
            </div>
          )}
          <button
            data-testid="theme-toggle-button"
            className="modern-icon-btn"
            onClick={toggle}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} weight="duotone" /> : <Moon size={16} weight="duotone" />}
          </button>
          {user && user !== false && (
            <button
              data-testid="navbar-logout-button"
              className="modern-icon-btn"
              onClick={handleLogout}
              aria-label="Logout"
              title="Logout"
            >
              <SignOut size={16} weight="duotone" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
